import { ColorMatrixFilter, Sprite } from 'pixi.js';
import type { SmashOffer, SmashSmashed, SmashState } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { canSmash, SMASH_EXPLAIN, smashCounters, smashedAt, smashLabel, smashLead } from '../../logic/minigames/smash';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { ROAD_SOUNDS } from '../../logic/road/sounds';
import { displayStyle, prefersReducedMotion, uiStyle } from '../board/ui';
import { CounterRow, gridCells, Piece } from './pieces';
import { TurnGameDialog } from './TurnGameDialog';
import type { MiniGameDeps } from './types';

/**
 * Smash: nine toadstools, each hiding coins, a ×2 or nothing. Tap one to
 * smash it; the server says what was inside. After the last smash the
 * coins found pay, doubled by a ×2. The toadstools not smashed stay whole
 * (a little faded): nothing says what they held. Tapping the counters or a
 * ×2 says what they mean.
 */
export class SmashDialog extends TurnGameDialog<SmashOffer, SmashState, SmashSmashed> {
  private pieces: Piece[] = [];
  private doubles: Sprite[] = [];
  private readonly counters: CounterRow;
  private readonly faded = new ColorMatrixFilter();

  constructor(deps: MiniGameDeps) {
    super(deps, 'smash', 'SMASH THREE');
    this.counters = new CounterRow(['Smashes left', 'Total'], (at) =>
      this.explain(SMASH_EXPLAIN.counters(this.offer?.setup?.smashes ?? 3), at),
    );
    this.faded.saturate(-0.35);
    this.faded.brightness(0.8, true);
    this.content.addChild(this.counters);
  }

  protected begin(offer: SmashOffer): void {
    this.pieces.forEach((p) => p.destroy({ children: true }));
    this.doubles.forEach((d) => d.destroy());
    const n = offer.setup?.objects ?? 9;
    this.pieces = Array.from({ length: n }, (_, i) => {
      const p = new Piece(() => this.onPiece(i));
      this.content.addChild(p);
      return p;
    });
    this.doubles = this.pieces.map(() => {
      const s = new Sprite();
      s.anchor.set(0.5);
      s.visible = false;
      this.content.addChild(s);
      return s;
    });
  }

  protected onPiece(index: number): void {
    const smashed = smashedAt(this.game, index);
    if (smashed?.kind === 'double') {
      const p = this.pieces[index];
      if (p) this.explain(SMASH_EXPLAIN.double, { x: p.x, y: p.y - p.h * 0.3 });
      return;
    }
    if (!canSmash(this.game, index)) return;
    this.act('smash', String(index), index);
    void this.pieces[index]?.bump();
  }

  protected override onAnswered(): void {
    const last = this.game.last;
    if (!last) return;
    AudioBus.shared.play(GAME_SOUNDS.smash, { volume: 0.85 });
    const after = last.kind === 'coins' ? ROAD_SOUNDS.coins[0] : last.kind === 'double' ? ROAD_SOUNDS.hiloRungUp : null;
    if (after) window.setTimeout(() => AudioBus.shared.play(after, { volume: 0.8 }), 160);
    const piece = this.pieces[last.index];
    if (piece) void piece.pop(piece.caption);
  }

  protected leadText(): string {
    return smashLead(this.game, this.offer?.setup?.smashes ?? 3);
  }

  protected pieceLabels(): string[] {
    return this.pieces.map((_, i) => {
      const s = smashedAt(this.game, i);
      return s ? `Toadstool ${i + 1}: ${smashLabel(s)}` : `Toadstool ${i + 1}`;
    });
  }

  protected renderPieces(y: number, width: number, maxHeight: number): number {
    const over = this.game.phase === 'over';
    // The counters step aside at the end: the amount says the total.
    const countersH = over ? 0 : this.counters.rowHeight + 8;
    const { cells, size, height } = gridCells(this.pieces.length, 3, width, 108, 6, 1, maxHeight - countersH);
    this.pieces.forEach((p, i) => {
      const s = smashedAt(this.game, i);
      const texture = this.deps.texture(s ? 'road:smash_broken' : 'road:smash_whole');
      p.paint(texture, size, size, (g, w, h) =>
        g.circle(0, -h * 0.08, w * 0.34).fill({ color: s ? 0x8a3a24 : 0xd8423a }).stroke({ color: 0x0b1838, width: 3 }),
      );
      p.position.set(cells[i]!.x, y + cells[i]!.y);
      p.cursor = canSmash(this.game, i) ? 'pointer' : s?.kind === 'double' ? 'help' : 'default';
      // Untouched toadstools fade once the smashing is over.
      p.filters = over && !s ? [this.faded] : [];
      p.alpha = over && !s ? 0.75 : 1;
      p.caption.visible = !!s && s.kind !== 'double';
      if (s) {
        p.caption.text = smashLabel(s);
        p.caption.style = s.kind === 'empty' ? uiStyle(15, 0xe8d8b8) : displayStyle(Math.round(size * 0.24));
        p.caption.position.set(0, -size * 0.06);
      }
      const d = this.doubles[i]!;
      d.visible = s?.kind === 'double';
      if (d.visible) {
        const tex = this.deps.texture('road:smash_double');
        if (tex) d.texture = tex;
        d.width = d.height = size * 0.6;
        d.position.set(p.x, p.y - size * 0.08);
      }
    });
    const c = smashCounters(this.game, this.offer?.setup?.smashes ?? 3);
    this.counters.visible = !over;
    if (over) return y + height;
    this.counters.set([String(c.left), c.total.toLocaleString('en-GB')], width);
    this.counters.position.set(0, y + height + 8);
    return y + height + countersH;
  }

  protected override animate(): void {
    if (prefersReducedMotion() || this.game.phase !== 'play') return;
    // Untouched toadstools sway a little, inviting a tap.
    this.pieces.forEach((p, i) => {
      if (!smashedAt(this.game, i)) p.body.rotation = Math.sin(this.clock * 2.2 + i * 0.8) * 0.04;
    });
  }
}
